import Button from "components/buttons";
import DynamicFormInput from "components/form/form";
import Notes from "components/notes";
import PageTitle from "components/pageTitle";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import { ChangeEvent, useRef, useState } from "react";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";
import FileUpload from "components/FileUpload";
import {
  LEGAL_TRAINING_PROVISION_REQUEST_STATUS,
  TokenPayload,
} from "utils/constantValues";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  createLegalTrainingProvision,
  updateLegalTrainingProvision,
} from "services/legalTrainingProvision";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";

const CreateLegalTrainingProvisionRequest = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const contextData = useAuth();
  const location = useLocation();
  const editData = location.state?.request;

  const [formState, setFormState] = useState({
    request_letter_ref_no: editData?.request_letter_ref_no || "",
    written_request_text: editData?.written_request_text || "",
    request_letter_files: null as File | null,
    directive_file: null as File | null,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const fileInputRefRequestLetter = useRef<HTMLInputElement>(null);
  const fileInputRefDirective = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (
    file: File | null,
    fieldName: keyof Pick<
      typeof formState,
      "request_letter_files" | "directive_file"
    >
  ) => {
    setFormState((prev) => ({ ...prev, [fieldName]: file }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formState.request_letter_ref_no)
      newErrors.request_letter_ref_no = t("RequestLetterReferenceNumberRequired") || "Request letter reference number is required.";

    if (!formState.written_request_text)
      newErrors.written_request_text = t("LegalTrainingNeedsDescriptionRequired") || "Description of legal training needs is required.";

    if (!formState.request_letter_files)
      newErrors.request_letter_files = t("OfficialRequestLetterRequired") || "Official request letter is required.";

    if (!formState.directive_file)
      newErrors.directive_file = t("SupportingTrainingDocumentRequired") || "Supporting training document is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast(t("PleaseFillAllRequiredFields") || "Please fill all required fields.", "error");
      return;
    }

    setSubmitting(true);
    const data = new FormData();

    Object.keys(formState).forEach((key) => {
      const value = formState[key as keyof typeof formState];
      if (value !== null) {
        data.append(key, value instanceof File ? value : String(value));
      }
    });

    data.append(
      "status",
      LEGAL_TRAINING_PROVISION_REQUEST_STATUS.submitted.value
    );

    try {
      let response;

      if (editData) {
        response = await updateLegalTrainingProvision(editData.id, data);
        showToast(t("LegalTrainingRequestUpdatedSuccess") || "Legal training request updated successfully!", "success");
      } else {
        const decoded = jwtDecode<TokenPayload>(
          contextData.user.access_token || ""
        );

        data.append(
          "requested_by",
          contextData.user?.user_type === "external"
            ? "Stakeholder"
            : contextData.user?.user_type
        );
        data.append("requested_by_id", contextData.user?.uu_id ?? "");
        data.append("first_name", decoded?.first_name || "");
        data.append("last_name", decoded?.last_name || "");
        data.append("email_user_organization", decoded?.email || "");
        data.append(
          "organization_kebele",
          decoded?.organization?.address?.kebele || ""
        );
        data.append(
          "organization_zone",
          decoded?.organization?.address?.zone || ""
        );
        data.append(
          "organization_state",
          decoded?.organization?.address?.state || ""
        );
        data.append("organization_name", decoded?.organization?.name || "");
        data.append("organization_type", decoded?.organization?.type || "");

        response = await createLegalTrainingProvision(data);
        showToast(
          t("LegalTrainingRequestSubmittedSuccess") || "Legal training provision request submitted successfully!",
          "success"
        );
      }

      if (response?.id) {
        navigate(`/legal-training-provision/${response.id}`);
      }

      if (fileInputRefRequestLetter.current)
        fileInputRefRequestLetter.current.value = "";
      if (fileInputRefDirective.current)
        fileInputRefDirective.current.value = "";
    } catch (error) {
      console.error(error);
      showToast(t("FailedToSubmitLegalTrainingRequest") || "Failed to submit legal training request.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageTitle
        icon="⚖"
        text={t("LegalTrainingProvisionRequest") || "Legal Training Provision Request"}
        subtitle={t("LegalTrainingProvisionSubtitle") || "Submit a formal request outlining institutional legal training needs and required supporting documents."}
        iconColor="text-purple-600"
        textColor="text-[#432101]"
      />

      <form onSubmit={handleSubmit}>
        <TwoColumnLayout
          left={
            <div className="py-[22px] px-[24px] bg-white rounded-xl space-y-4">
              <DynamicFormInput
                inputs={[
                  {
                    value: formState.request_letter_ref_no,
                    type: "text",
                    label: t("RequestLetterReferenceNumberRequired") || "Request Letter Reference Number *",
                    name: "request_letter_ref_no",
                    onChange: handleChange,
                    placeholder: t("RequestLetterReferenceNumberPlaceholder") || "e.g. LTP/REQ/2025/014",
                    error: errors.request_letter_ref_no,
                  },
                  {
                    value: formState.written_request_text,
                    type: "textarea",
                    label: t("LegalTrainingNeedsDescription") || "Description of Legal Training Needs *",
                    name: "written_request_text",
                    onChange: handleChange,
                    placeholder: t("LegalTrainingNeedsDescriptionPlaceholder") || "Describe the training objectives, target participants, and expected institutional outcomes.",
                    error: errors.written_request_text,
                  },
                ]}
              />

              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <FileUpload
                    label={t("OfficialRequestLetter") || "Official Request Letter *"}
                    onChange={(file) =>
                      handleFileChange(file, "request_letter_files")
                    }
                  />
                  {errors.request_letter_files && (
                    <span className="text-red-500 text-sm">
                      {errors.request_letter_files}
                    </span>
                  )}
                </div>

                <div className="flex-1">
                  <FileUpload
                    label={t("SupportingTrainingDocument") || "Supporting Training Document *"}
                    onChange={(file) =>
                      handleFileChange(file, "directive_file")
                    }
                  />
                  {errors.directive_file && (
                    <span className="text-red-500 text-sm">
                      {errors.directive_file}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-4">
                <Button variant="secondary" onClick={() => navigate(-1)}>
                  {t("Cancel") || "Cancel"}
                </Button>

                <Button type="submit" variant="primary">
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      {LoadingIcon} {t("Submitting") || "Submitting..."}
                    </div>
                  ) : (
                    t("SubmitTrainingRequest") || "Submit Training Request"
                  )}
                </Button>
              </div>
            </div>
          }
          right={
            <div className="w-full max-w-md space-y-4">
              <Notes
                title={t("SubmissionGuidelines") || "Submission Guidelines"}
                text={t("SubmissionGuidelinesText") || "Ensure all required documents and adequate justification are provided."}
                subtext={t("SubmissionGuidelinesSubtext") || "Incomplete submissions may be returned for revision."}
              />
            </div>
          }
        />
      </form>
    </div>
  );
};

export default CreateLegalTrainingProvisionRequest;